/**
 * Short reads on Jyotish - append entries; {@link JyotishaStoriesContent} renders cards.
 *
 * @typedef {Object} JyotishaStorySection
 * @property {string} headingHi
 * @property {string} [headingEn]
 * @property {string[]} paragraphsHi
 * @property {string[]} [paragraphsEn]
 *
 * @typedef {Object} JyotishaStory
 * @property {string} id
 * @property {string} topicId
 * @property {string} cardLabel
 * @property {number} [readMinutes]
 * @property {string} titleHi
 * @property {string} [titleEn]
 * @property {string} chipHi
 * @property {string} [chipEn]
 * @property {string} hookHi
 * @property {string} [hookEn]
 * @property {Array<keyof import('./jyotishaGrahaDevtImages').GRAHA_DEVTA_IMAGE>} planets
 * @property {string} [openingQuoteHi]
 * @property {string} [openingQuoteEn]
 * @property {JyotishaStorySection[]} sections
 * @property {string} takeawayHi
 * @property {string} [takeawayEn]
 * @property {string} jyotishNoteHi
 * @property {string} [jyotishNoteEn]
 */

/** @type {JyotishaStory[]} */
export const JYOTISHA_STORIES = [
  {
    id: 'chaya-putra-shani',
    topicId: 'jyotisha-stories-chaya-putra',
    cardLabel: 'छायापुत्र शनि',
    readMinutes: 9,
    titleHi: 'छायापुत्र शनि - जब सूर्य की पत्नी अपनी छाया छोड़ गई',
    titleEn: 'Son of Shadow: the birth of Saturn from Chaya',
    chipHi: 'पौराणिक कथा · सूर्य और शनि',
    chipEn: 'Puranic roots · Sun & Saturn',
    hookHi:
      'संज्ञा सूर्य की अग्नि सह न सकी - तो उसने अपनी छाया में प्राण डाले और गाय बनकर धरती पर चली गई। वही छाया माँ बनी। उसका तीसरा पुत्र शनि था। उसकी दृष्टि में दिन का प्रकाश कभी नरम नहीं रहा।',
    hookEn:
      'Sanjana could not live beside the Sun’s blaze - so she breathed life into her own shadow and vanished. That shadow became a mother. Her third child was Shani.',
    planets: ['Sun', 'Saturn'],
    sections: [
      {
        headingHi: 'प्रकाश जिसे सह न सका',
        headingEn: 'A brilliance she could not bear',
        paragraphsHi: [
          'संज्ञा विवस्वान की पत्नी थीं - स्वयं सूर्यदेव की - और त्वष्टा की पुत्री, जिन्होंने ब्रह्मांड का वास्तु रचा। वे तेजस्वी, कुशल और भक्त थीं। पर दिन-रात वे ऐसी अग्नि के पास खड़ी रहतीं जो कभी शांत न होती। सूर्य की ताप क्रूरता नहीं थी; वह उनका स्वभाव था। फिर भी धैर्य की भी सीमा होती है।',
          'स्वर्ग में कोई पत्नी विश्व के स्वामी को छोड़कर चल नहीं देती। इसलिए संज्ञा ने वह मार्ग अपनाया जो तीनों लोकों में पहले किसी ने नहीं चुना: अपनी छाया को बुलाया, प्राण दिया, और कहा - "अब तुम मैं हो। मेरी जगह संभालो। मेरा भेद न खोलना।"',
          'फिर वे रानी की छवि में न रहीं - एक घोड़ी बनकर धरती की ओर दौड़ गईं, और बिस्तर पर अपनी सही जैसी प्रतिमा छोड़ गईं।',
        ],
      },
      {
        headingHi: 'छाया की मातृत्व',
        headingEn: 'Chaya takes the throne of motherhood',
        paragraphsHi: [
          'छाया - अर्थात छाया ही - संज्ञा का जीवन इतनी पूरी तरह संभालने लगी कि सूर्य को शुरू में संदेह भी न हुआ। उसने तीन संतानें जन्मीं। पर प्रेम उसके हृदय में बँटा: अपने बच्चों को ज्यादा, संज्ञा की धारा वाले बच्चों को कम।',
          'यम, मृत्यु और न्याय के देवता, ने यह अंशपूर्णता देखी। एक दिन क्रोध या घायल अहंकार में उन्होंने माता से अशिष्ट वचन कहे। छाया का क्रोध तत्काल और पूर्ण था - वह क्रोध जो रहस्य और झुठ पर पला जाता है।',
          'उसने शाप दिया: "तेरा पैर सड़कर गिर जाए।"',
          'मृत्यु के देवता - यम - अपनी ही छाया-माँ से शापित हो गए। सूर्य ने शाप को नरम किया, पर मन में एक काँटा रह गया: कौन सी माँ अपने पुत्र को ऐसा शाप देती है?',
        ],
      },
      {
        headingHi: 'सत्य का भेद खुलता है',
        headingEn: 'The confession',
        paragraphsHi: [
          'सूर्य ने छाया को बुलाया और सत्य माँगा। काँपती हुई उसने सब स्वीकार कर लिया - प्रतिरूप, पलायन, धरती पर घोड़ी। विवस्वान ने उसे अलग रखा और असली पत्नी के पीछे खुद घोड़े का रूप धारण करके धरती पर चले।',
          'छाया की संतान आकाश की लेखा में रही:',
          'सावर्णि - भावी युग के मनु; तापती - पवित्र अग्नि में लिपटी नदी-देवी; और शनि - जो प्रकाश से नहीं, छाया से जन्मा।',
        ],
      },
      {
        headingHi: 'ज्योतिष में छायापुत्र',
        headingEn: 'Chayaputra in the chart',
        paragraphsHi: [
          'ज्योतिष में शनि को छायापुत्र कहते हैं - छाया का पुत्र। यह केवल काव्य नहीं; स्वभाव का संकेत है। जहाँ सूर्य दृढ़ता से आगे बढ़ता है, शनि रोकता है। जहाँ सूर्य बाहर फैलता है, शनि भीतर मोड़ लेता है - धीमा और भारी।',
          'पुराण कहते हैं जब शनि ने आँखें खोलीं, उसकी दृष्टि सूर्य के सारथी पर पड़ी - जाँघ टूट गई। फिर सूर्य के रथ के सात घोड़ों पर - वे अंधे हो गए। छाया का बालक, प्रकाश के सेवकों की ओर देखे, तो नरम दृष्टि कैसे रहे?',
          'इसीलिए लगभग हर शास्त्रीय मित्रता-सारणी में सूर्य और शनि शत्रु माने जाते हैं। यह केवल कुंडली पर चिपकाया अंधविश्वास नहीं; यह उष्मा और संयम, दृश्यता और विलंब, पिता और उस पुत्र की पुरानी कथा है जो दिन के उजाले से पूरी तरह जन्मा ही नहीं था।',
        ],
      },
    ],
    takeawayHi:
      'अगली बार कुंडली में सूर्य-शनि का तनाव देखें, तो स्मरण रखें: एक आकाश का सिंहासन सँभालता है; दूसरा उस पत्नी की छाया का स्मरण लिए फिरता है जिसे सूर्य की अग्नि से बचना पड़ा था।',
    takeawayEn:
      'Next time you see Sun-Saturn tension in a chart, remember: one rules the visible throne of the sky; the other carries the memory of a wife who had to become shadow to survive the blaze.',
    jyotishNoteHi:
      'शास्त्र शनि को छायापुत्र कहते हैं। ग्रह-मित्रता में सूर्य-शनि शत्रुता इसी पौराणिक धारा की झलक है - राशि, भाव और दशा के साथ पढ़ें, अकेले नहीं।',
    jyotishNoteEn:
      'Classical texts name Saturn Chayaputra. Sun-Saturn enmity in graha-mitra tables reflects this puranic strand - read alongside sign, house, and dasha.',
  },
  {
    id: 'brihaspati-karka-uccha',
    topicId: 'jyotisha-stories-brihaspati-karka-uccha',
    cardLabel: 'बृहस्पति कर्क में उच्च',
    readMinutes: 10,
    titleHi: 'बृहस्पति कर्क में उच्च क्यों हैं?',
    chipHi: 'प्रतीक-दृष्टि · गुरु, चंद्र और बुध',
    hookHi:
      'यह कथा किसी बाहरी कलह की नहीं, बल्कि भीतर घटती सूक्ष्म प्रक्रिया की है - बृहस्पति बुद्धि-तत्त्व हैं, तारा उस बुद्धि की क्रियाशील अभिव्यक्ति, चंद्र मन और बुध सीखने-समझने की क्षमता।',
    hookEn:
      'This is not merely an outer scandal; it points to an inner process where Jupiter is wisdom, Tara its living application, Moon the mind, and Mercury the power to learn and understand.',
    planets: ['Jupiter', 'Moon'],
    openingQuoteHi:
      'वैदिक परंपरा के अनुसार चंद्र ने बृहस्पति की पत्नी तारा को अपने साथ ले लिया, और उनसे बुध का जन्म हुआ - ऐसी वंश-रेखा जिसे लेकर बृहस्पति के मन में प्रसिद्ध रूप से असंतोष माना जाता है। फिर भी ज्योतिष के ब्रह्मांडीय विधान में बृहस्पति अपनी सर्वोच्च, सबसे दीप्तिमान उच्च अवस्था कर्क में प्राप्त करते हैं - और कर्क का स्वामी वही चंद्र है। तो फिर गुरु अपनी परम महिमा उसी के घर में क्यों पाते हैं जिसे व्यक्तिगत स्तर पर उनका सबसे बड़ा विश्वासघाती माना जाता है?',
    openingQuoteEn:
      "According to Vedic lore, the Moon (Chandra) eloped with Jupiter's wife, Tara, resulting in the birth of Mercury-a lineage Jupiter famously resents. Yet, in the cosmic order of astrology, Jupiter reaches his highest, most brilliant state of exaltation in Cancer-the sign ruled by none other than the Moon. Why does the Guru achieve his ultimate glory in the home of his greatest personal betrayer?",
    sections: [
      {
        headingHi: 'पुराण भीतर खुलते हैं',
        paragraphsHi: [
          'पुराण और उपनिषद की कथाएँ केवल आकाशीय पात्रों का इतिहास नहीं; वे उन सूक्ष्म शक्तियों के रूपक हैं जो समय-समय पर हमारे भीतर सक्रिय होती हैं। सूर्य आत्मा का, चंद्र मन का और बृहस्पति धी, विवेक और गुरु-बुद्धि का प्रतीक हैं।',
          'देवता और उनकी शक्ति या संगिनी को साथ पढ़ें तो अर्थ और स्पष्ट होता है। देवता किसी ऊर्जा के स्थिर, संभावनात्मक पक्ष को दर्शाते हैं; उनकी संगिनी उसी ऊर्जा के गतिशील, क्रियाशील पक्ष को।',
        ],
      },
      {
        headingHi: 'तारा, चंद्र और बुध का रहस्य',
        paragraphsHi: [
          'यदि बृहस्पति ज्ञान हैं, तो तारा उस ज्ञान का प्रयोग हैं - बुद्धि को व्यवहार में उतारने की शक्ति। स्वाभाविक है कि मन उसी ओर खिंचता है जहाँ समझ अनुभव बनती है; इसलिए चंद्र का तारा की ओर आकर्षण इस आंतरिक प्रक्रिया का संकेत है।',
          'जब मन और प्रयुक्त बुद्धि मिलते हैं, तब बुध जन्म लेते हैं - अर्थात सीखने की क्षमता, विश्लेषण, भाषा, समझ और शास्त्र-अध्ययन की प्रतिभा। इसलिए यह कथा किसी सांसारिक कलंक की नहीं, चेतना के सूक्ष्म खेल की है।',
        ],
      },
      {
        headingHi: 'बृहस्पति का विशाल हृदय',
        paragraphsHi: [
          'इस दृष्टि से देखें तो बृहस्पति ने न तारा से द्वेष रखा, न चंद्र से। उन्होंने बुध को अपना पुत्र मानकर शिक्षित किया। इसका संकेत यह है कि हमारी अध्ययन-शक्ति को अंततः विवेकपूर्ण बुद्धि ही दिशा देती है।',
          'इसी कारण बृहस्पति गुरु-तत्त्व के शिरोमणि माने जाते हैं और बुध श्रेष्ठ शिष्य। ग्रह-मित्रता सारणी में दोनों का शत्रु या सम संबंध उनके कार्य-भेद को दिखाता है, न कि किसी सतही व्यक्तिगत रंजिश को।',
        ],
      },
      {
        headingHi: 'कर्क में बृहस्पति उच्च क्यों',
        paragraphsHi: [
          'मूलत्रिकोण और उच्च राशि को ऐसे समझा जा सकता है जैसे किसी ग्रह का कर्म-स्थान और प्रिय-विश्राम-स्थान। बृहस्पति का मूलत्रिकोण धनु है - प्राकृतिक राशि चक्र का नवम भाव - जहाँ धर्म, सिद्धांत और आचार की रक्षा उनका कार्यक्षेत्र है।',
          'लेकिन कर्क प्राकृतिक चतुर्थ भाव है: शांति, आश्रय, हृदय, सुकून और पोषण का क्षेत्र। ज्ञान जब सुरक्षित, कोमल और ग्रहणशील वातावरण पाता है, तब वह सबसे सहज, उदार और फलदायी रूप में खिलता है; इसलिए बृहस्पति यहाँ उच्च माने गए।',
        ],
      },
      {
        headingHi: 'मंगल और बुध से तुलना',
        paragraphsHi: [
          'यही कारण है कि मंगल कर्क में नीच माने गए। मंगल भीतर की अग्नि, अभियान और कर्म-प्रेरणा हैं; उन्हें विश्राम-प्रधान, घरनुमा 4था भाव सहज नहीं लगता। उनका उत्कर्ष तो मकर जैसे कर्मप्रधान क्षेत्र में अधिक स्वाभाविक दिखता है।',
          'उधर बुध का मूलत्रिकोण और उच्चत्व कन्या में है - प्राकृतिक षष्ठ भाव के क्षेत्र में, जहाँ समस्या, विश्लेषण, तर्क, वाद-विवाद और सुधार की जगह है। सीखने की क्षमता चुनौतियों से निखरती है; इसलिए बुध वहाँ अपना श्रेष्ठ काम करते हैं।',
        ],
      },
    ],
    takeawayHi:
      'जब भी पूछें, “बृहस्पति कर्क में उच्च क्यों हैं?”, उत्तर केवल राशि-सारणी में नहीं, इस संकेत में खोजें: ज्ञान का श्रेष्ठ फूल वहीं खिलता है जहाँ भीतर शांति, पोषण और ग्रहणशीलता हो।',
    jyotishNoteHi:
      'कुंडली में गुरु को केवल “शुभ” कहकर न छोड़ें। देखें कि आपके भीतर गुरु-तत्त्व कहाँ काम करता है, मन उससे कैसे जुड़ता है, और बुध उस ज्ञान को सीखने, बोलने और समझने में कैसे बदलता है।',
  },
];

/** @param {string} topicId */
export function getJyotishaStoryByTopicId(topicId) {
  return JYOTISHA_STORIES.find((story) => story.topicId === topicId) ?? null;
}

/** Lesson cards under कथाएँ (Stories) - one tab per story. */
export function getJyotishaStoryTabs() {
  return JYOTISHA_STORIES.filter((story) => story.topicId).map((story) => ({
    id: story.topicId,
    label: story.cardLabel || story.titleHi,
    labelHi: story.cardLabel || story.titleHi,
    labelEn: story.titleEn || story.cardLabel || story.titleHi,
  }));
}

/** @param {string} topicId */
export function getJyotishaStoryReadMinutes(topicId) {
  const story = getJyotishaStoryByTopicId(topicId);
  return story?.readMinutes ?? 9;
}
